import { useState, useRef, useEffect, useCallback } from 'react';
import { trpc } from '../../lib/trpc';

export interface Region {
  _id: string;
  name: string;
  slug: string;
  country: string;
  coordinates: {
    type: 'Point';
    coordinates: [number, number];
  };
  defaultRadius: number;
  displayOrder: number;
  venueCount?: number;
}

export interface CityLocation {
  name: string;
  lat: number;
  lng: number;
}

interface RegionSearchProps {
  selectedRegion: Region | null;
  selectedCity: CityLocation | null;
  onRegionChange: (region: Region | null) => void;
  onCitySearch: (city: CityLocation) => void;
  onUseMyLocation: () => void;
  isLocating?: boolean;
  geoError?: string | null;
}

export const RegionSearch = ({
  selectedRegion,
  selectedCity,
  onRegionChange,
  onCitySearch,
  onUseMyLocation,
  isLocating = false,
  geoError = null,
}: RegionSearchProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sessionTokenRef = useRef<string>(crypto.randomUUID());
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const utils = trpc.useUtils();

  // Sync query text from parent selection (region or city picked). The ref
  // tracks the current selection's name so a cleared selection can be told
  // apart from text the user has typed.
  const selectionNameRef = useRef('');
  useEffect(() => {
    if (selectedRegion) {
      setQuery(selectedRegion.name);
      selectionNameRef.current = selectedRegion.name;
    } else if (selectedCity) {
      setQuery(selectedCity.name);
      selectionNameRef.current = selectedCity.name;
    } else {
      // Selection cleared (e.g. "Use my current location"). Wipe the box only
      // if it still shows the old selection label — keep user-typed text.
      setQuery((q) => (q === selectionNameRef.current ? '' : q));
      selectionNameRef.current = '';
    }
  }, [selectedRegion, selectedCity]);

  // The search query sent to the region.search endpoint — only when actively searching
  // (not when the input just shows a selected name)
  const hasSelection = !!selectedRegion || !!selectedCity;
  const searchQuery = hasSelection ? '' : query;

  // Debounce the search query for Places autocomplete (200ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 200);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data: allRegions } = trpc.region.list.useQuery();
  const { data: searchResults } = trpc.region.search.useQuery(
    { query: searchQuery },
    { enabled: searchQuery.length >= 1 }
  );

  const { data: placesData } = trpc.places.autocomplete.useQuery(
    { query: debouncedQuery, sessionToken: sessionTokenRef.current },
    { enabled: debouncedQuery.length >= 2 }
  );

  const regions = (searchQuery.length >= 1 ? searchResults : allRegions) as Region[] | undefined;

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (region: Region) => {
      onRegionChange(region);
      setIsOpen(false);
    },
    [onRegionChange]
  );

  const handlePlacesSelect = useCallback(
    async (placeId: string) => {
      try {
        const { name, lat, lng } = await utils.places.geocode.fetch({
          placeId,
          sessionToken: sessionTokenRef.current,
        });
        onCitySearch({ name, lat, lng });
        // Rotate the session token after a successful selection (Google billing rule).
        sessionTokenRef.current = crypto.randomUUID();
        setIsOpen(false);
      } catch {
        // Silent failure — user can pick a curated region or try again.
      }
    },
    [onCitySearch, utils]
  );

  // Clear everything — text, selection. Rotate the Places session token so the
  // next round of autocomplete starts a fresh billing session.
  const handleClear = useCallback(() => {
    onRegionChange(null); // also clears city via parent
    setQuery('');
    sessionTokenRef.current = crypto.randomUUID();
    inputRef.current?.focus();
  }, [onRegionChange]);

  // When user edits text, clear the current selection so we go back to searching
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setQuery(val);
      setIsOpen(true);
      // If there was a selection, clear it — user is now typing a new search
      if (hasSelection) {
        onRegionChange(null);
      }
    },
    [hasSelection, onRegionChange]
  );

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-xs font-medium text-gray-500 mb-1">Where?</label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => {
            if (!hasSelection) setIsOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (hasSelection) return;
              if (regions && regions.length > 0) {
                handleSelect(regions[0] as Region);
              } else if (
                // Only act on Places suggestions when the debounced query has caught up
                // to what the user typed — otherwise we'd select a stale match.
                placesData?.suggestions &&
                placesData.suggestions.length > 0 &&
                debouncedQuery === query.trim()
              ) {
                handlePlacesSelect(placesData.suggestions[0].placeId);
              }
            }
          }}
          placeholder="Region or city..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500"
        />
        {query.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Clear text"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Use my location button */}
      <button
        type="button"
        onClick={onUseMyLocation}
        disabled={isLocating}
        className="mt-1.5 flex items-center text-xs text-primary-700 hover:text-primary-800 disabled:text-gray-400"
      >
        <svg className="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {isLocating ? 'Getting location...' : 'Use my current location'}
      </button>

      {geoError && (
        <p className="mt-1 text-xs text-error-700" role="alert">
          {geoError}
        </p>
      )}

      {/* Dropdown */}
      {isOpen && !hasSelection && (
        <ul className="absolute z-20 mt-1 w-full bg-gray-50 border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          {regions && regions.length > 0 && (
            <>
              <li className="px-3 py-1 text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                Wine regions
              </li>
              {regions.map((region) => (
                <li key={region._id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(region as Region)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-800 flex justify-between items-center"
                  >
                    <span>
                      {region.name}
                      <span className="text-xs text-gray-400 ml-1">({region.country})</span>
                    </span>
                    {region.venueCount !== undefined && region.venueCount > 0 && (
                      <span className="text-xs text-gray-500">· {region.venueCount}</span>
                    )}
                  </button>
                </li>
              ))}
            </>
          )}

          {placesData?.suggestions && placesData.suggestions.length > 0 && (
            <>
              <li className="px-3 py-1 mt-1 text-xs uppercase tracking-wide text-gray-400 border-y border-gray-100 bg-gray-100/50">
                Cities
              </li>
              {placesData.suggestions.map((s) => (
                <li key={s.placeId}>
                  <button
                    type="button"
                    onClick={() => handlePlacesSelect(s.placeId)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-800"
                  >
                    <span>{s.label}</span>
                    <span className="text-xs text-gray-400 ml-1">{s.secondaryLabel}</span>
                  </button>
                </li>
              ))}
            </>
          )}

          {(!regions || regions.length === 0) &&
            (!placesData?.suggestions || placesData.suggestions.length === 0) &&
            debouncedQuery.length >= 2 && (
            <li className="px-3 py-2 text-sm text-gray-500">No matches</li>
          )}
        </ul>
      )}
    </div>
  );
};
